import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

export type AccountSession = {
  device?: string;
  clients?: Record<string, string>;
  id: string;
  is_current?: boolean;
  last_active?: string;
  location?: string;
  ipAddress?: string;
  lastAccess?: number;
  start?: number;
  userId?: string;
  username?: string;
};

type AccountSessionsState = {
  loading: boolean;
  deletingAll: boolean;
  deletingIds: string[];
  error: boolean;
  errorMessage: string;
  data: AccountSession[];
};

const initialState: AccountSessionsState = {
  loading: false,
  deletingAll: false,
  deletingIds: [],
  error: false,
  errorMessage: '',
  data: [],
};

const normalizeSessions = (data: any): AccountSession[] => {
  const payload = data?.response ?? data ?? [];
  if (!Array.isArray(payload)) {
    return [];
  }
  return payload.filter((item) => item && typeof item === 'object');
};

export const getAccountSessions = createAsyncThunk<AccountSession[]>(
  'getAccountSessions',
  async () => {
    const { data } = await api.get('/api/account/sessions');
    return normalizeSessions(data);
  }
);

export const deleteAccountSession = createAsyncThunk<
  string,
  { sessionId: string }
>('deleteAccountSession', async ({ sessionId }) => {
  if (!sessionId) {
    throw new Error('Missing session ID.');
  }
  await api.delete(`/api/account/sessions/${sessionId}`);
  return sessionId;
});

export const deleteAllAccountSessions = createAsyncThunk<void>(
  'deleteAllAccountSessions',
  async () => {
    await api.delete('/api/account/sessions');
  }
);

const accountSessionsSlice = createSlice({
  name: 'accountSessions',
  initialState,
  reducers: {
    resetAccountSessions: (state) => {
      state.loading = false;
      state.deletingAll = false;
      state.deletingIds = [];
      state.error = false;
      state.errorMessage = '';
      state.data = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAccountSessions.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getAccountSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(getAccountSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to load account sessions.';
    });
    builder.addCase(deleteAccountSession.pending, (state, action) => {
      const sessionId = action.meta.arg.sessionId;
      if (sessionId && !state.deletingIds.includes(sessionId)) {
        state.deletingIds.push(sessionId);
      }
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(deleteAccountSession.fulfilled, (state, action) => {
      const sessionId = action.payload;
      state.deletingIds = state.deletingIds.filter((id) => id !== sessionId);
      state.data = state.data.filter((session) => session.id !== sessionId);
    });
    builder.addCase(deleteAccountSession.rejected, (state, action) => {
      const sessionId = action.meta.arg.sessionId;
      state.deletingIds = state.deletingIds.filter((id) => id !== sessionId);
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to log out from session.';
    });
    builder.addCase(deleteAllAccountSessions.pending, (state) => {
      state.deletingAll = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(deleteAllAccountSessions.fulfilled, (state) => {
      state.deletingAll = false;
      state.data = [];
    });
    builder.addCase(deleteAllAccountSessions.rejected, (state, action) => {
      state.deletingAll = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to log out from all sessions.';
    });
  },
});

export const { resetAccountSessions } = accountSessionsSlice.actions;
export default accountSessionsSlice.reducer;
